<template>
  <Transition name="fade">
    <div
        v-if="visible"
        class="fixed inset-0 z-50 flex items-center flex-col p-8"
        :class="{
            'radial-background': step === 1,
            'bg-wg3-red': step !== 1,
            'justify-center': step === 1,
        }"
    >
      <template v-if="step === 1">
        <button
            @click="handleDismiss"
            class="close-button"
            aria-label="Close"
        >
          <img src="@/public/rating/close.svg" alt="Close" />
        </button>
        <h2 class="text-white font-bold mb-4 text-center">
          {{ t("rating.title") }}
        </h2>

        <h3 class="text-xl text-white w-full max-w-md font-bold mb-4 text-center" style="font-size: 20px; font-weight: 390;">
          {{ t("rating.description") }}
        </h3>

        <div class="flex gap-4 justify-center">
          <button @click="setRating(1)" class="text-5xl">
            <img src="@/public/rating/rating-1.svg" alt="Very bad" class="w-12 h-12" />
          </button>

          <button @click="setRating(2)" class="text-5xl">
            <img src="@/public/rating/rating-2.svg" alt="Bad" class="w-12 h-12" />
          </button>

          <button @click="setRating(3)" class="text-5xl">
            <img src="@/public/rating/rating-3.svg" alt="Okay" class="w-12 h-12" />
          </button>

          <button @click="setRating(4)" class="text-5xl">
            <img src="@/public/rating/rating-4.svg" alt="Good" class="w-12 h-12" />
          </button>

          <button @click="step = 3" class="text-5xl">
            <img src="@/public/rating/rating-5.svg" alt="Excellent" class="w-12 h-12" />
          </button>
        </div>
      </template>

      <template v-else-if="step === 2">
        <button
            @click="handleCloseNegative"
            class="close-button"
            aria-label="Close"
        >
          <img src="@/public/rating/close.svg" alt="Close" />
        </button>

        <h2 class="text-white w-full max-w-md font-bold mt-24 mb-4 text-center">
          {{ t("rating.feedback_title") }}
        </h2>

        <form @submit.prevent="submit" class="w-full max-w-md flex flex-col gap-4 text-left">
          <div>
            <label class="font-bold text-white" style="font-size: 20px; font-weight: bold;">{{ t("rating.feedback_text_title_1") }}</label>
            <textarea v-model="form.feedback" class="input-form comment" />
          </div>

          <div>
            <label class="font-bold text-white" style="font-size: 20px; font-weight: bold;">{{ t("rating.feedback_text_title_2") }}</label>
            <input v-model="form.email" class="input-form" />
          </div>

          <button
              type="submit"
              :disabled="!canSubmit"
              :class="['submit-button', !canSubmit ? 'opacity-50 cursor-not-allowed' : '']"
          >
            {{ submitting ? t("rating.sending") : t("rating.button") }}
          </button>
          <button class="mt-4 underline text-white text-m" type="button" @click="handleDismiss">
            {{ t("rating.cancel") }}
          </button>
        </form>
      </template>

      <template v-else-if="step === 3" class="justify-start">
        <button
            @click="handleClose"
            class="close-button"
            aria-label="Close"
        >
          <img src="@/public/rating/close.svg" alt="Close" />
        </button>

        <h2 class="text-white w-full max-w-md font-bold mt-16 mb-4 text-center line-40">
          {{ t("rating.step_title") }}
        </h2>

        <h3 class="text-white w-full max-w-md font-bold mb-4 text-center">
          {{ t("rating.step_description") }}
        </h3>

        <button @click="handleRedirect" class="submit-button mt-8" style="width: 267px">
          {{ t("rating.step_primary_button") }}
        </button>

        <span class="text-white w-full max-w-md font-bold mt-4 text-center" style="font-size: 12px; font-weight: 390;">
          {{ t("rating.step_primary_note") }}
        </span>

        <button class="mt-8 text-white underline text-m" @click="handleClose">
          {{ t("rating.step_secondary_button") }}
        </button>
      </template>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useOnboardingStore } from "@/stores/onboarding";
import { useJourneyStore } from "@/stores/journey";
import { t } from "@/lib/i18n";
import { reactive } from "vue";

const API_BASE = import.meta.env.VITE_API_BASE;
const now = ref(Date.now());
const step = ref(1);
const visible = ref(false);
const submitting = ref(false);
const journeyStore = useJourneyStore();
const parseOrDefault = (val: string | null | undefined) => {
  const n = Number(val);
  return (val == null || Number.isNaN(n) || n === 0) ? 99999 : n;
};
const triggerMinutes = parseOrDefault(t("rating.wait_minutes"));
const snoozeMinutes = parseOrDefault(t("rating.snooze_minutes"));
const redirectUrl = t("rating.positive_link") || '';
const store = useOnboardingStore();

const canSubmit = computed(() => {
  return form.rating1 > 0 && form.feedback.trim() !== '' && !submitting.value;
});

const form = reactive({
  rating1: 0,
  feedback: "",
  email: "",
  jua_journey_public_id: journeyStore?.journey?.public_id || '1234567890'
});

function setRating(value: number) {
  form.rating1 = value;
  step.value = 2;
}

async function submit() {
  if (submitting.value) return;

  submitting.value = true;

  try {
    const res = await fetch(`${API_BASE}/api/jua_journeys/submit_rating`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        rating: form,
        translations: {
          subject: t("rating.email_subject_negative"),
          museum_name: t("rating.museum_name"),
          title: t("rating.title"),
          text_title_1: t("rating.text_title_1"),
          text_title_2: t("rating.text_title_2"),
          receiver_email: t("rating.receiver_email"),
        }
      }),
    });

    const data = await res.json();

    if (!res.ok || !data.success) {
      console.error("Error saving rating:", data.errors);
      alert("Failed to send ratings.");
      return;
    }

    visible.value = false;
    store.setRated();
  } catch (e) {
    console.error("Network error:", e);
    alert("Network error. Try again later");
  } finally {
    submitting.value = false;
  }
}

async function submitPositiveOnly() {
  try {
    await fetch(`${API_BASE}/api/jua_journeys/submit_rating`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        rating: {
          rating1: 5,
          feedback: "",
          email: "",
          jua_journey_public_id: form.jua_journey_public_id,
        },
        translations: {
          subject: t("rating.email_subject_positive"),
          museum_name: t("rating.museum_name"),
          title: t("rating.title"),
          text_title_1: t("rating.text_title_1"),
          text_title_2: t("rating.text_title_2"),
          receiver_email: t("rating.receiver_email"),
        }
      }),
    });
  } catch (e) {
    console.error("Failed to send positive-only rating:", e);
  }
}

const shouldShow = computed(() => {
  if (store.rated || !store.initialTime) return false;
  if (store.snoozeUntil && store.snoozeUntil > now.value) return false;

  const elapsedMinutes = (now.value - store.initialTime) / 1000 / 60;
  return elapsedMinutes >= triggerMinutes;
});

onMounted(() => {
  if (!store.initialTime) {
    store.setInitialTime();
  }
  setInterval(() => {
    now.value = Date.now();
    if (shouldShow.value && !visible.value && journeyStore?.journey !== null) {
      visible.value = true;
    }
  }, 10000);
});

function handleDismiss() {
  visible.value = false;
  store.snooze(snoozeMinutes);
}

function handleClose() {
  submitPositiveOnly();
  visible.value = false;
  store.setRated();
}

function handleCloseNegative() {
  submit();
  visible.value = false;
  store.setRated();
}

async function handleRedirect() {
  await submitPositiveOnly();
  visible.value = false;
  store.setRated();
  window.location.href = redirectUrl!;
}
</script>

<style scoped>
* {
  font-family: 'Brandon Grotesque', sans-serif;
}

h2 {
  font-size: 35px;
}
h3 {
  font-size: 20px;
  font-weight: 390;
}
.radial-background {
  background: radial-gradient(circle at center, #FF003C 80%, white 80%, white 85%, #FF003C 85%, #FF003C 90%, white 90%, white 95%, #FF003C 95%) no-repeat;
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
}

.input-form {
  background-color: #FF003C;
  color: white;
  border: 2px solid white;
  width: 100%;
  padding: 0.5rem;
}

.comment {
  height: 90px;
}

.close-button {
  position: absolute;
  right: 32px;
  top: 32px;
  height: 40px;
  width: 40px;
}
.input-form:active, .input-form:focus{
  outline: none;
  box-shadow: none;
}

.submit-button {
  background-color: white;
  color: #FF003C;
  padding-block: 0.5rem;
  font-weight: bold;
  border-radius: 20px;
  font-size: 18px;
}

h2 {
  line-height: 40px;
}

.line-40 {
  line-height: 40px;
}
</style>
